/**
 * Automation Rules Page
 * Build and manage ticket automation rules with conditions and actions
 */

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, Zap, X, Play, Pause } from 'lucide-react';
import { formatDateTime } from '@/lib/utils';

interface Condition {
  field: string;
  operator: string;
  value: string;
}

interface Action {
  actionType: string;
  value: string;
}

interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  triggerEvent: string;
  conditions: Condition[];
  actions: Action[];
  isActive: boolean;
  executionOrder: number;
  executionCount: number;
  lastTriggeredAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const triggerEventLabels: Record<string, string> = {
  ticket_created: 'Ticket Created',
  ticket_updated: 'Ticket Updated',
  ticket_replied: 'Ticket Replied',
  sla_approaching: 'SLA Approaching',
  ticket_idle: 'Ticket Idle',
  ticket_resolved: 'Ticket Resolved',
};

const triggerEventColors: Record<string, string> = {
  ticket_created: 'bg-green-100 text-green-800',
  ticket_updated: 'bg-blue-100 text-blue-800',
  ticket_replied: 'bg-purple-100 text-purple-800',
  sla_approaching: 'bg-orange-100 text-orange-800',
  ticket_idle: 'bg-yellow-100 text-yellow-800',
  ticket_resolved: 'bg-gray-100 text-gray-800',
};

const conditionFields = [
  { value: 'priority', label: 'Priority' },
  { value: 'status', label: 'Status' },
  { value: 'category', label: 'Category' },
  { value: 'channel', label: 'Channel' },
  { value: 'subject', label: 'Subject' },
  { value: 'application_id', label: 'Application ID' },
];

const conditionOperators = [
  { value: 'equals', label: 'Equals' },
  { value: 'not_equals', label: 'Not Equals' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In' },
];

const actionTypes = [
  { value: 'set_priority', label: 'Set Priority' },
  { value: 'set_status', label: 'Set Status' },
  { value: 'assign_to', label: 'Assign To' },
  { value: 'add_tag', label: 'Add Tag' },
  { value: 'set_category', label: 'Set Category' },
  { value: 'add_internal_note', label: 'Add Internal Note' },
  { value: 'send_notification', label: 'Send Notification' },
];

const triggerEvents = [
  'ticket_created',
  'ticket_updated',
  'ticket_replied',
  'sla_approaching',
  'ticket_idle',
  'ticket_resolved',
];

export function AutomationRulesPage() {
  const queryClient = useQueryClient();

  // Filters
  const [triggerFilter, setTriggerFilter] = useState<string | undefined>();
  const [activeFilter, setActiveFilter] = useState<boolean | undefined>();
  const [page, setPage] = useState(1);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingRule, setDeletingRule] = useState<AutomationRule | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formTriggerEvent, setFormTriggerEvent] = useState('ticket_created');
  const [formIsActive, setFormIsActive] = useState(true);
  const [formExecutionOrder, setFormExecutionOrder] = useState('0');
  const [formConditions, setFormConditions] = useState<Condition[]>([]);
  const [formActions, setFormActions] = useState<Action[]>([]);

  // Fetch rules
  const { data, isLoading } = useQuery({
    queryKey: ['automationRules', { triggerEvent: triggerFilter, isActive: activeFilter, page }],
    queryFn: async () => {
      const result = await api.getAutomationRules({
        triggerEvent: triggerFilter,
        isActive: activeFilter,
        page,
        limit: 20,
      });
      return result;
    },
  });

  const rules = (data?.data || []) as AutomationRule[];
  const pagination = data?.pagination;

  function invalidateRules() {
    queryClient.invalidateQueries({ queryKey: ['automationRules'] });
  }

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => api.createAutomationRule(data),
    onSuccess: () => {
      invalidateRules();
      closeDialog();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.updateAutomationRule(id, data),
    onSuccess: () => {
      invalidateRules();
      closeDialog();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteAutomationRule(id),
    onSuccess: () => {
      invalidateRules();
      setDeleteDialogOpen(false);
      setDeletingRule(null);
    },
  });

  // Toggle active/inactive directly from the card
  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.updateAutomationRule(id, { isActive }),
    onSuccess: () => {
      invalidateRules();
    },
  });

  function openCreateDialog() {
    setEditingRule(null);
    setFormName('');
    setFormDescription('');
    setFormTriggerEvent('ticket_created');
    setFormIsActive(true);
    setFormExecutionOrder('0');
    setFormConditions([]);
    setFormActions([]);
    setDialogOpen(true);
  }

  function openEditDialog(rule: AutomationRule) {
    setEditingRule(rule);
    setFormName(rule.name);
    setFormDescription(rule.description || '');
    setFormTriggerEvent(rule.triggerEvent);
    setFormIsActive(rule.isActive);
    setFormExecutionOrder(String(rule.executionOrder));
    setFormConditions(rule.conditions && rule.conditions.length > 0 ? [...rule.conditions] : []);
    setFormActions(rule.actions && rule.actions.length > 0 ? [...rule.actions] : []);
    setDialogOpen(true);
  }

  function openDeleteDialog(rule: AutomationRule) {
    setDeletingRule(rule);
    setDeleteDialogOpen(true);
  }

  function closeDialog() {
    setDialogOpen(false);
    setEditingRule(null);
  }

  // Condition management
  function addCondition() {
    setFormConditions([...formConditions, { field: 'priority', operator: 'equals', value: '' }]);
  }

  function updateCondition(index: number, updates: Partial<Condition>) {
    const updated = [...formConditions];
    updated[index] = { ...updated[index], ...updates };
    setFormConditions(updated);
  }

  function removeCondition(index: number) {
    setFormConditions(formConditions.filter((_, i) => i !== index));
  }

  // Action management
  function addAction() {
    setFormActions([...formActions, { actionType: 'set_priority', value: '' }]);
  }

  function updateAction(index: number, updates: Partial<Action>) {
    const updated = [...formActions];
    updated[index] = { ...updated[index], ...updates };
    setFormActions(updated);
  }

  function removeAction(index: number) {
    setFormActions(formActions.filter((_, i) => i !== index));
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const payload: Record<string, unknown> = {
      name: formName,
      description: formDescription || null,
      triggerEvent: formTriggerEvent,
      conditions: formConditions,
      actions: formActions,
      isActive: formIsActive,
      executionOrder: parseInt(formExecutionOrder) || 0,
    };

    if (editingRule) {
      updateMutation.mutate({ id: editingRule.id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  }

  const isSaving = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Automation Rules</h1>
          <p className="text-muted-foreground">Automate ticket workflows with rules</p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="h-4 w-4 mr-2" />
          New Rule
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select
          value={triggerFilter || 'all'}
          onValueChange={(v) => {
            setTriggerFilter(v === 'all' ? undefined : v);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-52">
            <SelectValue placeholder="All Trigger Events" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Trigger Events</SelectItem>
            {triggerEvents.map((event) => (
              <SelectItem key={event} value={event}>
                {triggerEventLabels[event]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex gap-2">
          <Button
            variant={activeFilter === undefined ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => {
              setActiveFilter(undefined);
              setPage(1);
            }}
          >
            All
          </Button>
          <Button
            variant={activeFilter === true ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => {
              setActiveFilter(true);
              setPage(1);
            }}
          >
            Active
          </Button>
          <Button
            variant={activeFilter === false ? 'secondary' : 'outline'}
            size="sm"
            onClick={() => {
              setActiveFilter(false);
              setPage(1);
            }}
          >
            Inactive
          </Button>
        </div>
      </div>

      {/* Rules List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : rules.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Zap className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-medium mb-2">No automation rules found</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {triggerFilter || activeFilter !== undefined
                ? 'Try adjusting your filters'
                : 'Create your first automation rule to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {rules.map((rule) => (
            <Card
              key={rule.id}
              className="cursor-pointer hover:border-primary/50 transition-colors"
              onClick={() => openEditDialog(rule)}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{rule.name}</h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${triggerEventColors[rule.triggerEvent]}`}
                      >
                        {triggerEventLabels[rule.triggerEvent] || rule.triggerEvent}
                      </span>
                      <Badge variant={rule.isActive ? 'default' : 'secondary'}>
                        {rule.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {rule.description && (
                      <p className="text-sm text-muted-foreground mb-2">{rule.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        {rule.conditions?.length || 0} condition
                        {(rule.conditions?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      <span>
                        {rule.actions?.length || 0} action
                        {(rule.actions?.length || 0) !== 1 ? 's' : ''}
                      </span>
                      <span>Executed {rule.executionCount} times</span>
                      {rule.lastTriggeredAt && (
                        <span>Last triggered: {formatDateTime(rule.lastTriggeredAt)}</span>
                      )}
                    </div>
                  </div>

                  <div
                    className="flex items-center gap-1 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() =>
                        toggleActiveMutation.mutate({
                          id: rule.id,
                          isActive: !rule.isActive,
                        })
                      }
                      title={rule.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {rule.isActive ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(rule)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive"
                      onClick={() => openDeleteDialog(rule)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-muted-foreground">
                Page {page} of {pagination.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page === pagination.totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Rule Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent key={editingRule?.id || 'new'} className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Automation Rule' : 'New Automation Rule'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <div>
                <Label>Name *</Label>
                <Input
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Auto-assign urgent tickets"
                  required
                />
              </div>

              <div>
                <Label>Description</Label>
                <Textarea
                  value={formDescription}
                  onChange={(e) => setFormDescription(e.target.value)}
                  placeholder="Describe what this rule does..."
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Trigger Event *</Label>
                  <Select value={formTriggerEvent} onValueChange={setFormTriggerEvent}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {triggerEvents.map((event) => (
                        <SelectItem key={event} value={event}>
                          {triggerEventLabels[event]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Execution Order</Label>
                  <Input
                    type="number"
                    value={formExecutionOrder}
                    onChange={(e) => setFormExecutionOrder(e.target.value)}
                    min="0"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="active-checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label htmlFor="active-checkbox" className="mb-0 cursor-pointer">
                  Active
                </Label>
              </div>
            </div>

            {/* Conditions Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Conditions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addCondition}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Condition
                </Button>
              </div>

              {formConditions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No conditions defined. The rule will match all tickets for the trigger event.
                </p>
              ) : (
                <div className="space-y-2">
                  {formConditions.map((condition, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <Select
                        value={condition.field}
                        onValueChange={(v) => updateCondition(index, { field: v })}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conditionFields.map((f) => (
                            <SelectItem key={f.value} value={f.value}>
                              {f.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Select
                        value={condition.operator}
                        onValueChange={(v) => updateCondition(index, { operator: v })}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {conditionOperators.map((op) => (
                            <SelectItem key={op.value} value={op.value}>
                              {op.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        value={condition.value}
                        onChange={(e) => updateCondition(index, { value: e.target.value })}
                        placeholder="Value"
                        className="flex-1"
                      />

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-destructive"
                        onClick={() => removeCondition(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">Actions</Label>
                <Button type="button" variant="outline" size="sm" onClick={addAction}>
                  <Plus className="h-3 w-3 mr-1" />
                  Add Action
                </Button>
              </div>

              {formActions.length === 0 ? (
                <p className="text-sm text-muted-foreground py-2">
                  No actions defined. Add at least one action for the rule to do something.
                </p>
              ) : (
                <div className="space-y-2">
                  {formActions.map((action, index) => (
                    <div key={index} className="flex items-center gap-2 p-3 border rounded-lg bg-muted/30">
                      <Select
                        value={action.actionType}
                        onValueChange={(v) => updateAction(index, { actionType: v })}
                      >
                        <SelectTrigger className="w-48">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {actionTypes.map((at) => (
                            <SelectItem key={at.value} value={at.value}>
                              {at.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>

                      <Input
                        value={action.value}
                        onChange={(e) => updateAction(index, { value: e.target.value })}
                        placeholder="Value"
                        className="flex-1"
                      />

                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 shrink-0 text-destructive"
                        onClick={() => removeAction(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={closeDialog}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSaving || !formName}>
                {isSaving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Automation Rule</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deletingRule?.name}</strong>? This action
              cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => deletingRule && deleteMutation.mutate(deletingRule.id)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
